import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  ChevronLeft, Mail, Send, Users, MousePointer, Eye, 
  TrendingUp, TrendingDown, AlertTriangle, CheckCircle, 
  Info, XCircle, RefreshCw, ArrowUpRight, ArrowDownRight,
  BarChart3, Target, Zap, ChevronDown, ChevronUp, Copy,
  Download, AlertOctagon, Clock, Play, Pause, History,
  ExternalLink, Link2
} from 'lucide-react';
import { toast } from 'sonner';
import AdminLayout from '../components/AdminLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const insightTypeStyles = {
  success: {
    bg: 'bg-green-50 border-green-200',
    icon: CheckCircle,
    iconColor: 'text-green-600',
    titleColor: 'text-green-800',
    textColor: 'text-green-700'
  },
  info: {
    bg: 'bg-blue-50 border-blue-200',
    icon: Info,
    iconColor: 'text-blue-600',
    titleColor: 'text-blue-800',
    textColor: 'text-blue-700'
  },
  warning: {
    bg: 'bg-amber-50 border-amber-200',
    icon: AlertTriangle,
    iconColor: 'text-amber-600',
    titleColor: 'text-amber-800',
    textColor: 'text-amber-700'
  },
  error: {
    bg: 'bg-red-50 border-red-200',
    icon: XCircle,
    iconColor: 'text-red-600',
    titleColor: 'text-red-800',
    textColor: 'text-red-700'
  }
};

const MetricCard = ({ title, value, subtitle, icon: Icon, trend, benchmark, showBenchmark }) => {
  const isAboveBenchmark = benchmark && value > benchmark;
  const isBelowBenchmark = benchmark && value < benchmark;
  
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-500">{title}</p>
            <p className="text-3xl font-bold text-gray-900 mt-1">
              {typeof value === 'number' && title.includes('Rate') ? `${value.toFixed(1)}%` : value.toLocaleString()}
            </p>
            {subtitle && (
              <p className="text-xs text-gray-500 mt-1">{subtitle}</p>
            )}
            {showBenchmark && benchmark && (
              <div className={`flex items-center gap-1 mt-2 text-xs ${isAboveBenchmark ? 'text-green-600' : isBelowBenchmark ? 'text-amber-600' : 'text-gray-500'}`}>
                {isAboveBenchmark ? (
                  <ArrowUpRight className="w-3 h-3" />
                ) : isBelowBenchmark ? (
                  <ArrowDownRight className="w-3 h-3" />
                ) : null}
                <span>Benchmark: {benchmark}%</span>
              </div>
            )}
          </div>
          <div className="p-3 bg-[#00457f]/10 rounded-lg">
            <Icon className="w-6 h-6 text-[#00457f]" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const InsightCard = ({ insight }) => {
  const style = insightTypeStyles[insight.type] || insightTypeStyles.info;
  const Icon = style.icon;
  
  return (
    <div className={`p-4 rounded-lg border ${style.bg}`}>
      <div className="flex gap-3">
        <div className="flex-shrink-0 mt-0.5">
          <Icon className={`w-5 h-5 ${style.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${style.bg} ${style.titleColor}`}>
              {insight.metric}
            </span>
          </div>
          <h4 className={`font-semibold ${style.titleColor}`}>{insight.title}</h4>
          <p className={`text-sm mt-1 ${style.textColor}`}>{insight.message}</p>
          {insight.recommendation && (
            <div className="mt-3 pt-3 border-t border-current/10">
              <p className={`text-sm ${style.textColor}`}>
                <span className="font-medium">Recommendation:</span> {insight.recommendation}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Failure reason color mapping
const reasonColors = {
  "Hard Bounce (Invalid Address)": "bg-red-100 text-red-800",
  "Soft Bounce (Temporary Issue)": "bg-orange-100 text-orange-800",
  "Invalid Email Address": "bg-red-100 text-red-800",
  "Blocked/Spam Filter": "bg-purple-100 text-purple-800",
  "Suppressed/Unsubscribed": "bg-gray-100 text-gray-800",
  "Connection/Server Error": "bg-yellow-100 text-yellow-800",
  "Rate Limited": "bg-blue-100 text-blue-800",
  "Authentication Error": "bg-pink-100 text-pink-800",
  "Mailbox Full": "bg-amber-100 text-amber-800",
  "Bounced": "bg-orange-100 text-orange-800",
  "Other": "bg-gray-100 text-gray-700"
};

const FailedDeliveriesSection = ({ failedDeliveries }) => {
  const [expanded, setExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);
  
  if (!failedDeliveries || failedDeliveries.total === 0) {
    return null;
  }

  const { total, by_reason, details } = failedDeliveries;
  const displayedDetails = showAll ? details : details.slice(0, 10);

  const handleCopyEmails = () => {
    const emails = details.map(d => d.email).join('\n');
    navigator.clipboard.writeText(emails);
    toast.success(`Copied ${details.length} email addresses`);
  };

  const handleDownloadCSV = () => {
    const headers = ['Email', 'Error', 'Attempted At'];
    const rows = details.map(d => [
      d.email,
      d.error,
      d.attempted_at ? new Date(d.attempted_at).toISOString() : 'N/A'
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `failed_deliveries_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('Downloaded failed deliveries CSV');
  };

  return (
    <Card className="border-red-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-red-700">
            <AlertOctagon className="w-5 h-5" />
            Failed Deliveries ({total})
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleCopyEmails}
              className="text-xs"
              data-testid="copy-failed-emails-btn"
            >
              <Copy className="w-3 h-3 mr-1" />
              Copy Emails
            </Button>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleDownloadCSV}
              className="text-xs"
              data-testid="download-failed-csv-btn"
            >
              <Download className="w-3 h-3 mr-1" />
              Download CSV
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              data-testid="toggle-failed-details-btn"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      
      <CardContent>
        {/* Failure Reasons Summary */}
        <div className="mb-4">
          <p className="text-sm font-medium text-gray-700 mb-2">Failure Breakdown:</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(by_reason).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
              <span 
                key={reason}
                className={`px-3 py-1 rounded-full text-xs font-medium ${reasonColors[reason] || reasonColors.Other}`}
              >
                {reason}: {count}
              </span>
            ))}
          </div>
        </div>

        {/* Expandable Details Table */}
        {expanded && details.length > 0 && (
          <div className="mt-4 border rounded-lg overflow-hidden" data-testid="failed-deliveries-table">
            <div className="bg-gray-50 px-4 py-2 border-b">
              <p className="text-sm font-medium text-gray-700">
                Showing {displayedDetails.length} of {details.length} failed addresses
              </p>
            </div>
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Email</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Error</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {displayedDetails.map((item, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono text-xs text-gray-800">{item.email}</td>
                      <td className="px-4 py-2 text-xs text-gray-600 max-w-xs truncate" title={item.error}>
                        {item.error}
                      </td>
                      <td className="px-4 py-2 text-xs text-gray-500 whitespace-nowrap">
                        {item.attempted_at 
                          ? new Date(item.attempted_at).toLocaleString('en-US', { 
                              month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' 
                            })
                          : 'N/A'
                        }
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {details.length > 10 && (
              <div className="bg-gray-50 px-4 py-2 border-t">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowAll(!showAll)}
                  className="w-full text-xs"
                  data-testid="show-more-failed-btn"
                >
                  {showAll ? 'Show Less' : `Show All ${details.length} Failed Addresses`}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Call to action */}
        <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-sm text-amber-800">
            <strong>Tip:</strong> Download the CSV to review all failed addresses. Consider removing invalid emails 
            from your list or adding hard bounces to the suppression list to improve future deliverability.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// Batch Run History Section
const batchStatusColors = {
  scheduled: 'bg-blue-100 text-blue-800',
  running: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-green-100 text-green-800',
  failed: 'bg-red-100 text-red-800',
  skipped: 'bg-gray-100 text-gray-600'
};

const batchStatusIcons = {
  scheduled: Clock,
  running: Play,
  completed: CheckCircle,
  failed: XCircle,
  skipped: Pause
};

const BatchRunHistorySection = ({ campaignId, token }) => {
  const [batchRuns, setBatchRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchBatchRuns();
  }, [campaignId]);

  const fetchBatchRuns = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/batch-runs`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBatchRuns(data);
      }
    } catch (err) {
      console.error('Error fetching batch runs:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <div className="animate-spin w-6 h-6 border-2 border-[#00457f] border-t-transparent rounded-full mx-auto" />
        </CardContent>
      </Card>
    );
  }

  if (!batchRuns || batchRuns.length === 0) {
    return null;
  }

  const displayedRuns = expanded ? batchRuns : batchRuns.slice(0, 5);

  const formatDuration = (startedAt, completedAt) => {
    if (!startedAt || !completedAt) return '-';
    const start = new Date(startedAt);
    const end = new Date(completedAt);
    const durationMs = end - start;
    const seconds = Math.floor(durationMs / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  const totalSuccess = batchRuns.reduce((sum, b) => sum + (b.success_count || 0), 0);
  const totalFailure = batchRuns.reduce((sum, b) => sum + (b.failure_count || 0), 0);
  const completedBatches = batchRuns.filter(b => b.status === 'completed').length;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-[#00457f]" />
            Batch Run History
          </CardTitle>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-500">
              {completedBatches}/{batchRuns.length} batches completed
            </span>
            <span className="text-green-600 font-medium">{totalSuccess} sent</span>
            {totalFailure > 0 && (
              <span className="text-red-600 font-medium">{totalFailure} failed</span>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              data-testid="toggle-batch-history-btn"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto" data-testid="batch-history-table">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Batch #</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Status</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Scheduled</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Started</th>
                <th className="text-left px-4 py-3 font-medium text-gray-600">Duration</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Sent</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Failed</th>
                <th className="text-right px-4 py-3 font-medium text-gray-600">Failure Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {displayedRuns.map((batch) => {
                const StatusIcon = batchStatusIcons[batch.status] || Clock;
                return (
                  <tr key={batch.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      #{batch.batch_number}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${batchStatusColors[batch.status] || batchStatusColors.scheduled}`}>
                        <StatusIcon className="w-3 h-3" />
                        {batch.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {batch.scheduled_for 
                        ? new Date(batch.scheduled_for).toLocaleString('en-US', {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })
                        : '-'
                      }
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {batch.started_at 
                        ? new Date(batch.started_at).toLocaleString('en-US', {
                            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                          })
                        : '-'
                      }
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {formatDuration(batch.started_at, batch.completed_at)}
                    </td>
                    <td className="px-4 py-3 text-right text-green-600 font-medium">
                      {batch.success_count || 0}
                    </td>
                    <td className="px-4 py-3 text-right text-red-600 font-medium">
                      {batch.failure_count || 0}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`${
                        batch.failure_rate > 0.1 ? 'text-red-600' : 
                        batch.failure_rate > 0.05 ? 'text-amber-600' : 'text-gray-600'
                      }`}>
                        {((batch.failure_rate || 0) * 100).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {batchRuns.length > 5 && (
          <div className="mt-4 pt-4 border-t">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
              className="w-full text-sm"
            >
              {expanded ? 'Show Less' : `Show All ${batchRuns.length} Batches`}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Click Tracking Breakdown Section
const ClickTrackingSection = ({ campaignId, token }) => {
  const [trackingStats, setTrackingStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    fetchTrackingStats();
  }, [campaignId]);

  const fetchTrackingStats = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/tracking-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTrackingStats(data);
      }
    } catch (err) {
      console.error('Error fetching tracking stats:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-6 text-center">
          <div className="animate-spin w-6 h-6 border-2 border-[#00457f] border-t-transparent rounded-full mx-auto" />
        </CardContent>
      </Card>
    );
  }

  if (!trackingStats || (trackingStats.summary.total_opens === 0 && trackingStats.summary.total_clicks === 0)) {
    return null; // Don't show section if no tracking data
  }

  const { summary, click_breakdown, top_engaged_recipients } = trackingStats;

  const truncateUrl = (url, maxLength = 50) => {
    if (!url || url.length <= maxLength) return url;
    return url.substring(0, maxLength) + '...';
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <MousePointer className="w-5 h-5 text-[#00457f]" />
            Email Engagement Tracking
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded(!expanded)}
            data-testid="toggle-tracking-section-btn"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">{summary.unique_opens}</div>
            <div className="text-sm text-blue-700">Unique Opens</div>
            <div className="text-xs text-blue-500 mt-1">{summary.open_rate}% open rate</div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-green-600">{summary.total_opens}</div>
            <div className="text-sm text-green-700">Total Opens</div>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-purple-600">{summary.unique_clicks}</div>
            <div className="text-sm text-purple-700">Unique Clicks</div>
            <div className="text-xs text-purple-500 mt-1">{summary.click_rate}% click rate</div>
          </div>
          <div className="bg-amber-50 rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">{summary.total_clicks}</div>
            <div className="text-sm text-amber-700">Total Clicks</div>
            <div className="text-xs text-amber-500 mt-1">{summary.click_to_open_rate}% CTOR</div>
          </div>
        </div>

        {expanded && (
          <>
            {/* Click Breakdown by URL */}
            {click_breakdown && click_breakdown.length > 0 && (
              <div className="mb-6">
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <Link2 className="w-4 h-4" />
                  Click Breakdown by Link
                </h4>
                <div className="overflow-x-auto" data-testid="click-breakdown-table">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">URL</th>
                        <th className="text-right px-4 py-2 font-medium text-gray-600">Total Clicks</th>
                        <th className="text-right px-4 py-2 font-medium text-gray-600">Unique Clicks</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {click_breakdown.slice(0, 10).map((link, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <a 
                              href={link.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline flex items-center gap-1"
                              title={link.url}
                            >
                              {truncateUrl(link.url)}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </td>
                          <td className="px-4 py-2 text-right font-medium">{link.total_clicks}</td>
                          <td className="px-4 py-2 text-right text-gray-600">{link.unique_clicks}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Top Engaged Recipients */}
            {top_engaged_recipients && top_engaged_recipients.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Top Engaged Recipients
                </h4>
                <div className="overflow-x-auto" data-testid="top-engaged-table">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">Recipient</th>
                        <th className="text-right px-4 py-2 font-medium text-gray-600">Opens</th>
                        <th className="text-right px-4 py-2 font-medium text-gray-600">Clicks</th>
                        <th className="text-left px-4 py-2 font-medium text-gray-600">First Opened</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {top_engaged_recipients.slice(0, 10).map((recipient, idx) => (
                        <tr key={idx} className="hover:bg-gray-50">
                          <td className="px-4 py-2">
                            <div className="font-medium text-gray-900">{recipient.name || recipient.email}</div>
                            {recipient.name && <div className="text-xs text-gray-500">{recipient.email}</div>}
                          </td>
                          <td className="px-4 py-2 text-right">
                            <span className="inline-flex items-center gap-1">
                              <Eye className="w-3 h-3 text-blue-500" />
                              {recipient.opens}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right">
                            <span className="inline-flex items-center gap-1">
                              <MousePointer className="w-3 h-3 text-purple-500" />
                              {recipient.clicks}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-gray-600">
                            {recipient.first_opened 
                              ? new Date(recipient.first_opened).toLocaleString('en-US', {
                                  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                })
                              : '-'
                            }
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {!expanded && (click_breakdown?.length > 0 || top_engaged_recipients?.length > 0) && (
          <div className="text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(true)}
              className="text-sm"
            >
              Show detailed click breakdown
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const EmailCampaignAnalytics = () => {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchAnalytics();
  }, [token, campaignId]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${API_URL}/api/email/campaigns/${campaignId}/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin_user');
          toast.error('Session expired. Please log in again.');
          navigate('/admin/login');
          return;
        }
        throw new Error('Failed to fetch analytics');
      }

      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.message);
      toast.error('Failed to load campaign analytics');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      scheduled: 'bg-blue-100 text-blue-800',
      sending: 'bg-yellow-100 text-yellow-800',
      sent: 'bg-green-100 text-green-800',
      paused: 'bg-orange-100 text-orange-800',
      failed: 'bg-red-100 text-red-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || colors.draft}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  // Calculate key takeaway
  const getKeyTakeaway = () => {
    if (!analytics || !analytics.insights || analytics.insights.length === 0) {
      return null;
    }

    // Prioritize insights: error > warning > success > info
    const priorityOrder = ['error', 'warning', 'success', 'info'];
    const sortedInsights = [...analytics.insights].sort((a, b) => {
      return priorityOrder.indexOf(a.type) - priorityOrder.indexOf(b.type);
    });

    const topInsight = sortedInsights[0];
    
    // Generate a summary based on overall performance
    const metrics = analytics.metrics;
    const benchmarks = analytics.benchmarks;
    
    if (metrics.total_sent === 0) {
      return {
        type: 'info',
        message: 'This campaign is ready to be sent. Review your content and recipient list before launching.'
      };
    }

    const aboveBenchmarks = [];
    const belowBenchmarks = [];
    
    if (metrics.open_rate > benchmarks.open_rate) aboveBenchmarks.push('opens');
    else if (metrics.open_rate < benchmarks.open_rate) belowBenchmarks.push('opens');
    
    if (metrics.click_rate > benchmarks.click_rate) aboveBenchmarks.push('clicks');
    else if (metrics.click_rate < benchmarks.click_rate) belowBenchmarks.push('clicks');

    if (aboveBenchmarks.length >= 2) {
      return {
        type: 'success',
        message: `Excellent campaign! Your ${aboveBenchmarks.join(' and ')} exceeded industry benchmarks. This email resonated well with your audience.`
      };
    } else if (belowBenchmarks.length >= 2) {
      return {
        type: 'warning',
        message: `This campaign underperformed in ${belowBenchmarks.join(' and ')}. Consider testing different subject lines or content approaches.`
      };
    } else if (topInsight.type === 'error') {
      return {
        type: 'error',
        message: topInsight.message
      };
    } else {
      return {
        type: 'info',
        message: 'This campaign performed within expected ranges. Continue monitoring engagement trends across campaigns.'
      };
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-[#00457f] border-t-transparent rounded-full" />
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="p-8">
          <Card>
            <CardContent className="pt-6 text-center">
              <XCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900">Failed to Load Analytics</h3>
              <p className="text-gray-500 mt-2">{error}</p>
              <Button onClick={fetchAnalytics} className="mt-4">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </CardContent>
          </Card>
        </div>
      </AdminLayout>
    );
  }

  const keyTakeaway = getKeyTakeaway();

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link 
            to="/admin/email" 
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back to Campaigns
          </Link>
          
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#00457f] rounded-lg">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Campaign Analytics</h1>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-gray-600">{analytics?.campaign_name}</span>
                  {getStatusBadge(analytics?.status)}
                </div>
              </div>
            </div>
            <Button variant="outline" onClick={fetchAnalytics}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
          
          {analytics?.sent_at && (
            <p className="text-sm text-gray-500 mt-2">
              Sent on {new Date(analytics.sent_at).toLocaleDateString('en-US', {
                weekday: 'long',
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
          )}
        </div>

        {/* Key Takeaway Banner */}
        {keyTakeaway && (
          <Card className={`mb-6 border-l-4 ${
            keyTakeaway.type === 'success' ? 'border-l-green-500 bg-green-50' :
            keyTakeaway.type === 'warning' ? 'border-l-amber-500 bg-amber-50' :
            keyTakeaway.type === 'error' ? 'border-l-red-500 bg-red-50' :
            'border-l-blue-500 bg-blue-50'
          }`}>
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <Target className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                  keyTakeaway.type === 'success' ? 'text-green-600' :
                  keyTakeaway.type === 'warning' ? 'text-amber-600' :
                  keyTakeaway.type === 'error' ? 'text-red-600' :
                  'text-blue-600'
                }`} />
                <div>
                  <h3 className="font-semibold text-gray-900">Key Takeaway</h3>
                  <p className="text-gray-700 mt-1">{keyTakeaway.message}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <MetricCard
            title="Total Sent"
            value={analytics?.metrics?.total_sent || 0}
            subtitle="emails"
            icon={Send}
          />
          <MetricCard
            title="Delivered"
            value={analytics?.metrics?.delivered || 0}
            subtitle={`${analytics?.metrics?.delivery_rate || 0}% rate`}
            icon={CheckCircle}
          />
          <MetricCard
            title="Open Rate"
            value={analytics?.metrics?.open_rate || 0}
            subtitle={`${analytics?.metrics?.opened || 0} opens`}
            icon={Eye}
            benchmark={analytics?.benchmarks?.open_rate}
            showBenchmark={true}
          />
          <MetricCard
            title="Click Rate"
            value={analytics?.metrics?.click_rate || 0}
            subtitle={`${analytics?.metrics?.clicked || 0} clicks`}
            icon={MousePointer}
            benchmark={analytics?.benchmarks?.click_rate}
            showBenchmark={true}
          />
          <MetricCard
            title="Bounce Rate"
            value={analytics?.metrics?.bounce_rate || 0}
            subtitle={`${analytics?.metrics?.bounced || 0} bounced`}
            icon={XCircle}
            benchmark={analytics?.benchmarks?.bounce_rate}
            showBenchmark={true}
          />
          <MetricCard
            title="Unsubscribed"
            value={analytics?.metrics?.unsubscribed || 0}
            subtitle={`${analytics?.metrics?.unsubscribe_rate || 0}% rate`}
            icon={Users}
          />
        </div>

        {/* Additional Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Click-to-Open Rate</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {(analytics?.metrics?.click_to_open_rate || 0).toFixed(1)}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Of those who opened, this % clicked
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <Zap className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Failed Deliveries</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {analytics?.metrics?.failed || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Emails that couldn't be sent
                  </p>
                </div>
                <div className="p-3 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Recipients</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {analytics?.metrics?.total_recipients || 0}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    In original recipient list
                  </p>
                </div>
                <div className="p-3 bg-[#00457f]/10 rounded-lg">
                  <Users className="w-6 h-6 text-[#00457f]" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Batch Run History Section */}
        <div className="mb-6">
          <BatchRunHistorySection campaignId={campaignId} token={token} />
        </div>

        {/* Click Tracking Section */}
        <div className="mb-6">
          <ClickTrackingSection campaignId={campaignId} token={token} />
        </div>

        {/* Failed Deliveries Section */}
        {analytics?.failed_deliveries && analytics.failed_deliveries.total > 0 && (
          <div className="mb-6">
            <FailedDeliveriesSection failedDeliveries={analytics.failed_deliveries} />
          </div>
        )}

        {/* Insights Section */}
        {analytics?.insights && analytics.insights.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#00457f]" />
                Performance Insights
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analytics.insights.map((insight, index) => (
                  <InsightCard key={index} insight={insight} />
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default EmailCampaignAnalytics;
